import ArrowDropDown from '@mui/icons-material/ArrowDropDown';
import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { JobState } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { sortBy } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { FILTER_MENU_ITEM_HEIGHT_IN_PX } from '~components/Filters/FilterMenuItem';
import SearchField from '~components/Filters/SearchField';
import { OverflowAwareText } from '~components/Order/ordersDispatchStyledComponents';
import { EquipmentTypeItem } from '~hooks/useEquipment/models';
import useEquipment from '~hooks/useEquipment/useEquipment';
import { Job } from '~hooks/useJob/models';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';

export type RequestedEquipmentTypeSelectorOption = {
  name: string;
  accountingId: Nullable<string>;
};

interface RequestedEquipmentTypeSelectorProps {
  isDisabled?: boolean;
  job: Job;
  onChange: (requestedEquipmentType?: RequestedEquipmentTypeSelectorOption) => void;
  placeholder: string;
  requestedEquipmentType: Job['requestedEquipmentTypeName'];
}

const jobStatesAllowingEquipmentRemoval = [
  JobState.ACCEPTED,
  JobState.ASSIGNED,
  JobState.CREATED,
  JobState.SENT,
];

export default function RequestedEquipmentTypeSelector({
  isDisabled: isDisabledProp,
  job,
  onChange,
  placeholder,
  requestedEquipmentType,
}: RequestedEquipmentTypeSelectorProps) {
  const theme = useTheme();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const isOpen = Boolean(anchor);

  const removeEquipmentRef = useRef<SVGSVGElement | null>(null);
  const [searchFieldValue, setSearchFieldValue] = useState('');
  const [isConfirmingRemoval, setIsConfirmingRemoval] = useState(false);
  const [isLoadingEquipmentTypes, setIsLoadingEquipmentTypes] = useState(false);
  const [loadedEquipmentTypes, setLoadedEquipmentTypes] = useState<{
    companyId: string;
    equipmentTypes: EquipmentTypeItem[];
  } | null>(null);

  const { ordersDispatchStore } = useStores();
  const { getEquipmentTypesByCompanyId } = useEquipment();
  const jobCompanyId = job.company?.id;

  const loadedCompanyId = loadedEquipmentTypes?.companyId;

  useEffect(() => {
    const hasJobCompanyEquipmentTypes = loadedCompanyId === jobCompanyId;

    if (!isOpen || !jobCompanyId || hasJobCompanyEquipmentTypes) {
      return;
    }

    let isLatestRequest = true;

    const loadEquipmentTypes = async () => {
      setIsLoadingEquipmentTypes(true);

      try {
        const { data } = await getEquipmentTypesByCompanyId({
          companyId: jobCompanyId,
        });
        const keptEquipmentTypes = data.filter(
          (equipmentType) => !equipmentType.discardedAt,
        );

        if (isLatestRequest) {
          setLoadedEquipmentTypes({
            companyId: jobCompanyId,
            equipmentTypes: sortBy(keptEquipmentTypes, 'name'),
          });
        }
      } finally {
        if (isLatestRequest) {
          setIsLoadingEquipmentTypes(false);
        }
      }
    };

    loadEquipmentTypes().catch(() => {});

    return () => {
      isLatestRequest = false;
    };
  }, [isOpen, jobCompanyId, loadedCompanyId, getEquipmentTypesByCompanyId]);

  const isDisabled = Boolean(
    ordersDispatchStore.isUpdatingOrderJob.get(job.id) || isDisabledProp,
  );

  const jobCompanyEquipmentTypes =
    loadedCompanyId === jobCompanyId ? (loadedEquipmentTypes?.equipmentTypes ?? []) : [];

  const options = jobCompanyEquipmentTypes
    .filter((equipmentType) => {
      return equipmentType.name.toLowerCase().includes(searchFieldValue.toLowerCase());
    })
    .map((equipmentType) => ({
      name: equipmentType.name,
      accountingId: equipmentType.accountingId,
    }));

  const isEquipmentRemovable = Boolean(
    requestedEquipmentType && jobStatesAllowingEquipmentRemoval.includes(job.status),
  );

  if (isDisabled) {
    if (requestedEquipmentType) {
      return (
        <Tooltip title={requestedEquipmentType}>
          <OverflowAwareText>{requestedEquipmentType}</OverflowAwareText>
        </Tooltip>
      );
    }

    return null;
  }

  if (!job.editable) {
    if (requestedEquipmentType) {
      return <OverflowAwareText>{requestedEquipmentType}</OverflowAwareText>;
    }

    return null;
  }

  return (
    <>
      <ButtonBase
        disabled={isDisabled}
        sx={{
          alignItems: 'center',
          border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
          borderRadius: theme.spacing(0.5),
          gap: 0.5,
          height: 24,
          maxWidth: '100%',
          pl: 0.5,
          pr: requestedEquipmentType ? 0.5 : 0,
          backgroundColor: theme.semantic.surfaceRaised,
          '&.Mui-disabled': { opacity: 0.6, pointerEvents: 'auto' },
          '&:not(.Mui-disabled):hover': {
            backgroundColor: theme.brandV2.colors.treadGray10,
            borderColor: theme.brandV2.colors.treadGray3,
            '& .MuiSvgIcon-root': isEquipmentRemovable
              ? {
                  '&:hover': {
                    backgroundColor: theme.brandV2.colors.treadGray3,
                    color: theme.semantic.textInverse,
                  },
                }
              : { color: theme.brandV2.colors.treadGray2 },
          },
        }}
        onClick={(event) => {
          event.stopPropagation();

          if (event.target === removeEquipmentRef.current) {
            setAnchor(null);
            setIsConfirmingRemoval(true);
          } else {
            setAnchor(event.currentTarget);
          }
        }}
      >
        <OverflowAwareText sx={{ flex: 1 }}>
          {requestedEquipmentType ?? placeholder}
        </OverflowAwareText>

        {isEquipmentRemovable ? (
          <Tooltip title={t('actions.remove_entity', { entity: placeholder })}>
            <Close
              ref={removeEquipmentRef}
              sx={{
                backgroundColor: 'transparent',
                borderRadius: '50%',
                color: theme.brandV2.colors.treadGray2,
                flex: 0,
                fontSize: '12px',
                height: '13px',
                minWidth: '13px',
                width: '13px',
              }}
            />
          </Tooltip>
        ) : (
          <ArrowDropDown
            sx={{
              color: theme.brandV2.colors.treadGray4,
              flex: 0,
              fontSize: '18px',
              minWidth: '18px',
            }}
          />
        )}
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={isOpen}
        onClose={(event: React.MouseEvent) => {
          event.stopPropagation();
          setAnchor(null);
        }}
        sx={{
          '& .MuiPaper-root': {
            border: `solid 1px ${theme.brandV2.colors.treadGray7}`,
            boxShadow: '0px 1px 10px 0px rgba(60, 64, 67, 0.15)',
            mt: '3px',
            width: '250px',
          },
          '& .MuiItem-root': {
            px: 1.5,
          },
        }}
      >
        <SearchField
          isLoading={isLoadingEquipmentTypes}
          onChange={(value) => setSearchFieldValue(value)}
          setFocusOnMount
          value={searchFieldValue}
        />

        <Divider sx={{ '&.MuiDivider-root': { mt: 0, mb: 1, mx: 2 } }} />

        <Box
          sx={{
            maxHeight: FILTER_MENU_ITEM_HEIGHT_IN_PX * 5,
            overflow: 'auto',
          }}
        >
          {options.map((option) => (
            <MenuItem
              key={option.name}
              onClick={(event) => {
                event.stopPropagation();
                setAnchor(null);
                onChange(option);
              }}
              data-ui={`page-dispatch__equipment-type-selector__job-${job.jobId}__select-button`}
            >
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'space-between',
                  width: '100%',
                }}
              >
                <OverflowAwareText>{option.name}</OverflowAwareText>

                {option.name === requestedEquipmentType && (
                  <Check sx={{ fontSize: '16px' }} />
                )}
              </Box>
            </MenuItem>
          ))}
        </Box>
      </Menu>

      <ConfirmationDialog
        isOpen={isConfirmingRemoval}
        onClose={() => setIsConfirmingRemoval(false)}
        onConfirm={() => {
          setIsConfirmingRemoval(false);
          onChange();
        }}
        title={t('actions.confirm_action')}
      >
        <Typography>
          {t('dispatch.job.confirm_requested_equipment_type_removal', {
            entity: placeholder,
          })}
        </Typography>
      </ConfirmationDialog>
    </>
  );
}
